// src/logs/index.js
// Logs — extracted from index.html (v105.x sprint, auto-generated header).
// Bundled by esbuild into dist/logs.js. The HTML loads it via
//   <script src="dist/logs.js"></script>
// and call sites use `Logs.<sub>.<fn>()` (e.g.
//   Logs.templates.loadLogTemplatesFromDB()
//   Logs.entries.renderLogEntryCard()
// ).
//
// Backwards compat: index.html's trailing script block re-exposes the
// moved functions on window so existing call sites — both inline JS and
// onclick="" attributes — keep working without HTML edits.
//
// Free variables (DB, currentUser, currentStore, escapeHtml, WORKER_URL,
// fetch, console, document, window, ...) live in the inline globals
// scope of index.html. esbuild leaves them as bare references at runtime.

import '../shared/key-activate.js';   // v136.8: Enter/Space on the role="button" log entry cards
import { isWeeklyLog, logWeekRange, weeklyLogStatus } from './logFrequency.js';   // v129.96 — ONE weekly-log rule (Matt 9 Aug: any day, due Monday, once per week), shared with the Checks card

// D2 — logs follow the 3am business day: an entry made between 00:00 and 03:00
// belongs to the PREVIOUS trading day (matching the rota's 3am-3am business day
// and the checklist post-midnight rule). Falls back to the calendar date if the
// trading-day helpers aren't loaded. The stored entry_date AND the staff roster
// picker both use this so they always agree on which day the entry is for.
function logTradingDate() {
  if (typeof currentTradingDate === 'function') return currentTradingDate();
  if (typeof businessDayStr === 'function') return businessDayStr();
  return (typeof todayDateStr === 'function') ? todayDateStr() : null;
}

// v120.3 — NEVER LOSE AN UNSAVED LOG ENTRY.
// Two staff reported the "safe log" failing to save on a slow store connection
// ("background lagging… nothing saved"). When a save fails (usually a network
// timeout) the modal already keeps the typed values on screen, but if the
// person reloads or closes the app, the entry is gone. So on a failed save we
// stash the in-progress NEW entry to localStorage, keyed by store + log type.
// Next time they open a new entry of that type at that store, we restore it and
// ask them to check + re-save. Cleared on a successful save. Drafts expire after
// 24h so a stale one can't haunt a later shift. Edits aren't stashed (the
// original is safe in the DB).
var LOG_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
// Holds a restored draft for the NEW-entry modal; null when editing or none.
var logDraftToRestore = null;

function logDraftKey(store, logType) {
  return 'elc_log_draft::' + (store || '') + '::' + (logType || '');
}
function saveLogDraft(store, logType, draft) {
  try {
    localStorage.setItem(logDraftKey(store, logType), JSON.stringify({ ts: Date.now(), draft: draft }));
  } catch (e) { /* storage full / unavailable — nothing we can do */ }
}
function loadLogDraft(store, logType) {
  try {
    var raw = localStorage.getItem(logDraftKey(store, logType));
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || !parsed.draft || (Date.now() - (parsed.ts || 0)) > LOG_DRAFT_TTL_MS) {
      localStorage.removeItem(logDraftKey(store, logType));
      return null;
    }
    return parsed.draft;
  } catch (e) { return null; }
}
function clearLogDraft(store, logType) {
  try { localStorage.removeItem(logDraftKey(store, logType)); } catch (e) {}
}

// v120.4 — PERMANENT DUPLICATE GUARD (server-side idempotency, no migration).
// Each composed NEW entry gets a stable id, stamped into the entry's data as
// __client_uuid and reused across every save attempt (a re-tap, or a restored
// draft after a reload — see logDraftToRestore). The Worker checks for an
// existing row carrying the same id before inserting, so a save that timed out
// client-side but actually landed can't be turned into a duplicate by a retry.
// The id lives inside the existing `data` JSONB, so there is NO schema change
// and the rollout is safe in any deploy order.
var logEntryClientUuid = null;

function newLogUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through to the manual generator */ }
  return 'log-' + Date.now().toString(36) + '-' +
    Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
}

// ── templates ─────────────────────────────────────────────
export const templates = {};

async function loadLogTemplatesFromDB() {
  if (typeof DB === 'undefined') return;
  try {
    var rows = await DB.logTemplates.list();
    logTemplatesList = Array.isArray(rows) ? rows : [];
    dlog('[DB] Loaded ' + logTemplatesList.length + ' log templates');
  } catch(e) {
    console.warn('[DB] Log templates load failed:', e.message);
    logTemplatesList = [];
  }
}
templates.loadLogTemplatesFromDB = loadLogTemplatesFromDB;

function getActiveLogTemplates() {
  return (logTemplatesList || []).filter(function(t){ return t.active !== false; })
    .sort(function(a,b){
      var oa = a.sort_order || 0, ob = b.sort_order || 0;
      if (oa !== ob) return oa - ob;
      return (a.name || '').localeCompare(b.name || '');
    });
}
templates.getActiveLogTemplates = getActiveLogTemplates;

function getLogTemplate(type) {
  return (logTemplatesList || []).find(function(t){ return t.type === type; });
}
templates.getLogTemplate = getLogTemplate;

// ── entries ─────────────────────────────────────────────
export const entries = {};

function renderLogEntryCard(entry, tmpl) {
  var fields = (tmpl && Array.isArray(tmpl.fields)) ? tmpl.fields : [];
  var time = entry.entry_time ? String(entry.entry_time).slice(0,5) : '';
  var staff = entry.staff_name || entry.created_by || '—';
  var data = entry.data || {};

  var lines = [];
  fields.forEach(function(f){
    var v = data[f.key];
    if (v === null || v === undefined || v === '') return;
    // v135.1: what staff typed (the value, the notes, the name) is marked translate="no", never translated.
    lines.push('<span style="color:var(--sub)">' + escapeHtml(f.label) + ':</span> <strong translate="no">' + escapeHtml(String(v)) + '</strong>');
  });
  if (entry.notes) lines.push('<span style="color:var(--sub)">Notes:</span> <span translate="no">' + escapeHtml(entry.notes) + '</span>');

  // v136.8 (accessibility audit): role="button" and tabindex="0" so a keyboard can reach and open it (Enter/Space:
  // src/shared/key-activate.js)
  return '<div role="button" tabindex="0" style="background:var(--card);border-radius:10px;padding:10px 12px;margin-bottom:8px;box-shadow:0 1px 3px rgba(0,0,0,.05);cursor:pointer" onclick="editLogEntry(' + entry.id + ')">' +
    '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">' +
      '<div translate="no" style="font-size:12px;font-weight:700;color:var(--text)">' + escapeHtml(staff) + '</div>' +
      '<div style="font-size:11px;color:var(--sub)">' + time + '</div>' +
    '</div>' +
    '<div style="font-size:12px;line-height:1.6">' + lines.join(' · ') + '</div>' +
  '</div>';
}
entries.renderLogEntryCard = renderLogEntryCard;

function openLogEntryModal() {
  logEditingEntry = null;
  // v120.3 — offer back any entry that failed to save last time (slow network).
  logDraftToRestore = loadLogDraft(currentStore, currentLogType);
  // v120.4 — stable idempotency id for this composed entry. Reuse a restored
  // draft's id so a retry-after-reload dedupes against the timed-out original;
  // otherwise mint a fresh one.
  logEntryClientUuid = (logDraftToRestore && logDraftToRestore.client_uuid) || newLogUuid();
  renderLogEntryForm();
  document.getElementById('logEntryTitle').textContent = 'New Entry';
  document.getElementById('logEntrySaveBtn').textContent = 'Save';
  document.getElementById('logEntryModal').classList.add('open');
}
entries.openLogEntryModal = openLogEntryModal;

function editLogEntry(id) {
  var entry = logEntries.find(function(e){ return e.id === id; });
  if (!entry) return;
  logEditingEntry = entry;
  logDraftToRestore = null;   // editing a saved entry — never restore a draft
  logEntryClientUuid = null;  // edits go by row id (PATCH) — no idempotency id
  renderLogEntryForm();
  document.getElementById('logEntryTitle').textContent = 'Edit Entry';
  document.getElementById('logEntrySaveBtn').textContent = 'Update';
  document.getElementById('logEntryModal').classList.add('open');
}
entries.editLogEntry = editLogEntry;

function closeLogEntryModal() {
  document.getElementById('logEntryModal').classList.remove('open');
  logEditingEntry = null;
  logDraftToRestore = null;
  logEntryClientUuid = null;
}
entries.closeLogEntryModal = closeLogEntryModal;

function renderLogEntryForm() {
  var tmpl = getLogTemplate(currentLogType);
  var form = document.getElementById('logEntryForm');
  form.innerHTML = '';
  if (!tmpl) { form.innerHTML = '<div style="color:var(--danger)">Template not found</div>'; return; }

  // v120.3 — if we're restoring an entry that failed to save last time, tell
  // the user up front so they check it and re-save (rather than thinking it's a
  // fresh blank form).
  if (!logEditingEntry && logDraftToRestore) {
    var restoreBanner = document.createElement('div');
    restoreBanner.style.cssText = 'background:var(--warn-bg);border:1px solid var(--stores-warn-line);color:var(--warn-deep);border-radius:8px;padding:9px 11px;margin-bottom:12px;font-size:12px;line-height:1.5';
    restoreBanner.textContent = "This entry didn't save last time — we've brought it back. Please check the details and tap Save again.";
    form.appendChild(restoreBanner);
  }

  var data = logEditingEntry ? (logEditingEntry.data || {})
           : (logDraftToRestore && logDraftToRestore.data) ? logDraftToRestore.data : {};
  // v65: when creating a NEW entry, don't auto-fill TIME with current time —
  // that imposed meaning ("this is the entry time") which was wrong for use
  // cases like Visitor Log, where the time being recorded is usually the
  // *leave* time and should be typed manually. Edit-mode still prefills with
  // the existing entry_time so changes are explicit.
  var existingTime = logEditingEntry && logEditingEntry.entry_time
    ? String(logEditingEntry.entry_time).slice(0,5)
    : (logDraftToRestore && logDraftToRestore.entry_time ? String(logDraftToRestore.entry_time).slice(0,5) : '');
  var existingStaffId = logEditingEntry ? logEditingEntry.staff_id
    : (logDraftToRestore ? logDraftToRestore.staff_id : null);
  // v113.0 — roster date = the entry's date (today for a new entry), so the
  // picker can include anyone covering at this store that day.
  var rosterDateStr = (logEditingEntry && logEditingEntry.entry_date)
    ? logEditingEntry.entry_date
    : logTradingDate();   // D2: post-midnight → previous trading day's roster

  // Staff picker
  var staffRow = document.createElement('div');
  staffRow.style.cssText = 'margin-bottom:12px';
  staffRow.innerHTML = '<label style="display:block;font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Staff Member</label>';
  var staffSel = document.createElement('select');
  staffSel.id = 'logFieldStaff';
  staffSel.style.cssText = 'width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg)';
  staffSel.innerHTML = '<option value="">— Select —</option>';
  getStoreRoster(currentStore, rosterDateStr).forEach(function(s){
    var opt = document.createElement('option');
    opt.value = s.id;
    opt.textContent = s.name;
    opt.setAttribute('translate', 'no');   // v135.1: a name is never translated (saveLogEntry reads this text back)
    if (existingStaffId == s.id) opt.selected = true;
    staffSel.appendChild(opt);
  });
  staffRow.appendChild(staffSel);
  form.appendChild(staffRow);

  // Time
  var timeRow = document.createElement('div');
  timeRow.style.cssText = 'margin-bottom:12px';
  timeRow.innerHTML =
    '<label style="display:block;font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Time</label>' +
    '<input type="time" id="logFieldTime" value="' + existingTime + '" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg)">';
  form.appendChild(timeRow);

  // Template fields
  (tmpl.fields || []).forEach(function(f){
    var row = document.createElement('div');
    row.style.cssText = 'margin-bottom:12px';
    var label = f.label + (f.required ? ' <span style="color:var(--danger)">*</span>' : '');
    var val = data[f.key] != null ? data[f.key] : '';
    var input = '';
    var id = 'logField_' + f.key;
    if (f.type === 'textarea') {
      input = '<textarea id="' + id + '" rows="2" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg);resize:vertical">' + escapeHtml(val) + '</textarea>';
    } else if (f.type === 'select' && Array.isArray(f.options)) {
      var opts = '<option value="">— Select —</option>' + f.options.map(function(o){
        return '<option value="' + escapeHtml(o) + '"' + (val === o ? ' selected' : '') + '>' + escapeHtml(o) + '</option>';
      }).join('');
      input = '<select id="' + id + '" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg)">' + opts + '</select>';
    } else if (f.type === 'yesno') {
      input = '<select id="' + id + '" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg)">' +
        '<option value="">—</option>' +
        '<option value="yes"' + (val === 'yes' ? ' selected' : '') + '>Yes</option>' +
        '<option value="no"'  + (val === 'no'  ? ' selected' : '') + '>No</option>' +
        '</select>';
    } else {
      var inputType = f.type || 'text';
      if (inputType === 'number') inputType = 'number';
      else if (inputType === 'date') inputType = 'date';
      else if (inputType === 'time') inputType = 'time';
      else inputType = 'text';
      // v80.16 — Visitor Log feedback fix.
      // For time-typed fields (e.g. "Time Out" on a Visitor Log) that are
      // empty WHILE EDITING an existing entry, auto-fill with the current
      // time. Typical flow: visitor signs in → manager creates entry with
      // arrival time, leaves Time Out blank. When the visitor leaves, the
      // manager opens the entry → Time Out pre-populates to NOW so they
      // can save with one tap. The manager can still override before saving.
      // We don't auto-fill on NEW entries (visitor hasn't left yet) or when
      // the field already has a value (don't overwrite history).
      if (inputType === 'time' && !val && logEditingEntry) {
        var nowD = new Date();
        val = String(nowD.getHours()).padStart(2,'0') + ':' + String(nowD.getMinutes()).padStart(2,'0');
      }
      input = '<input type="' + inputType + '" id="' + id + '" value="' + escapeHtml(val) + '" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg)">';
    }
    row.innerHTML = '<label style="display:block;font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">' + label + '</label>' + input;
    form.appendChild(row);
  });

  // Notes
  var notesRow = document.createElement('div');
  notesRow.style.cssText = 'margin-bottom:12px';
  notesRow.innerHTML =
    '<label style="display:block;font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.4px;margin-bottom:4px">Notes (optional)</label>' +
    '<textarea id="logFieldNotes" rows="2" style="width:100%;border:1.5px solid var(--border);border-radius:8px;padding:8px 10px;font-size:14px;font-family:inherit;background:var(--input-bg);resize:vertical">' + escapeHtml(logEditingEntry ? (logEditingEntry.notes || '') : (logDraftToRestore ? (logDraftToRestore.notes || '') : '')) + '</textarea>';
  form.appendChild(notesRow);

  // Delete button for existing entries
  if (logEditingEntry) {
    var delRow = document.createElement('div');
    delRow.style.cssText = 'margin-top:8px;padding-top:12px;border-top:1px solid var(--border)';
    delRow.innerHTML = '<button onclick="deleteLogEntry()" style="width:100%;background:none;color:var(--danger);border:1.5px solid var(--stores-danger-line);border-radius:8px;padding:8px;font-size:13px;font-family:inherit;cursor:pointer;font-weight:600">Delete This Entry</button>';
    form.appendChild(delRow);
  }
}
entries.renderLogEntryForm = renderLogEntryForm;

async function saveLogEntry() {
  var tmpl = getLogTemplate(currentLogType);
  if (!tmpl) return;

  var staffSel = document.getElementById('logFieldStaff');
  var staffId = staffSel.value ? parseInt(staffSel.value, 10) : null;
  var staffName = staffSel.value ? staffSel.options[staffSel.selectedIndex].text : '';
  if (!staffId) { showToast('Please select a staff member', 'error'); return; }

  var time = document.getElementById('logFieldTime').value;

  // Collect template fields
  var data = {};
  var missingRequired = null;
  (tmpl.fields || []).forEach(function(f){
    var el = document.getElementById('logField_' + f.key);
    if (!el) return;
    var v = el.value;
    if (f.type === 'number') {
      v = v === '' ? null : Number(v);
    } else if (v === '') {
      v = null;
    }
    if (f.required && (v === null || v === '')) missingRequired = f.label;
    if (v !== null) data[f.key] = v;
  });
  if (missingRequired) { showToast('Please fill in: ' + missingRequired, 'error'); return; }

  // v120.4 — stamp the stable idempotency id into a NEW entry's data so the
  // Worker can recognise a retry of the same save and refuse to duplicate it.
  // Edits go by row id (PATCH), so they don't carry one.
  if (!logEditingEntry && logEntryClientUuid) data.__client_uuid = logEntryClientUuid;

  var notes = document.getElementById('logFieldNotes').value.trim();

  var payload = {
    log_type: currentLogType,
    store: currentStore,
    // v65: when editing, preserve the original entry_date — overwriting it
    // with today silently moved past entries to today's date, making them
    // appear to "disappear" from the user's view. (Spinneys visitor-log
    // bug, April 28.) Same for created_by — should record the original
    // author, not whoever last edited.
    // D2 — new entries date to the 3am business day (00:00-03:00 → prev day);
    // edits preserve the original entry_date (v65 Spinneys fix).
    entry_date: logEditingEntry ? (logEditingEntry.entry_date || logTradingDate()) : logTradingDate(),
    entry_time: time || null,
    staff_id: staffId,
    staff_name: staffName,
    data: data,
    notes: notes || null,
    created_by: logEditingEntry ? (logEditingEntry.created_by || (currentUser ? currentUser.name : staffName)) : (currentUser ? currentUser.name : staffName),
  };

  var btn = document.getElementById('logEntrySaveBtn');
  var prev = logEditingEntry ? 'Update' : 'Save';   // v135.1: the English label, not the on-screen (maybe Arabic) text
  btn.disabled = true; btn.textContent = 'Saving…';

  try {
    if (logEditingEntry) {
      await DB.logs.update(logEditingEntry.id, payload);
      showToast('Entry updated', 'success');
    } else {
      var saved = await DB.logs.create(payload);
      showToast('Entry saved', 'success');
      // v129.86 — the petty-cash approval-code cross-check (warn, never
      // block): the save succeeded; any quoted code that doesn't match an
      // approved ticket is surfaced so the till can fix it now, not at
      // month-end reconciliation.
      if (saved && saved._petty_cash_warnings && saved._petty_cash_warnings.length) {
        saved._petty_cash_warnings.forEach(function (w) { showToast('💵 ' + w, 'error'); });
      }
      // v120.3 — saved cleanly; drop any restored/failed draft for this slot.
      clearLogDraft(currentStore, currentLogType);
    }
    closeLogEntryModal();
    renderLogsScreen();
  } catch(e) {
    console.error('[DB] Log save failed:', e);
    showToast('Save failed: ' + (e.message || 'unknown'), 'error');
    // v120.3 — NEVER LOSE THE ENTRY. The modal already keeps the typed values
    // on screen, but a reload/close would lose them. Stash NEW entries so they
    // can be restored next time this log is opened at this store. (Edits aren't
    // stashed: the original row is safe in the DB.)
    if (!logEditingEntry) {
      saveLogDraft(currentStore, currentLogType, {
        staff_id:    payload.staff_id,
        staff_name:  payload.staff_name,
        entry_time:  payload.entry_time,
        data:        payload.data,
        notes:       payload.notes,
        // v120.4 — keep the idempotency id with the draft so a retry after a
        // reload dedupes against the (possibly already-saved) timed-out entry.
        client_uuid: logEntryClientUuid,
      });
    }
  } finally {
    // BUG FIX (May 1, 2026): re-enable the button on BOTH paths. Previously
    // only the catch block reset disabled=false; on success the button stayed
    // disabled forever. closeLogEntryModal() doesn't touch the button state,
    // and openLogEntryModal() only updates textContent (not disabled). So on
    // a successful save, the next time the modal opened the button was still
    // disabled and the user couldn't submit a second log. Real scenario:
    // two store visitors arrive at once, each needs their own log entry.
    btn.disabled = false; btn.textContent = prev;
  }
}
entries.saveLogEntry = saveLogEntry;

async function deleteLogEntry() {
  if (!logEditingEntry) return;
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Delete this log entry? This cannot be undone.', { danger: true, confirmLabel: 'Delete' }))) return;
  try {
    await DB.logs.remove(logEditingEntry.id);
    showToast('Entry deleted', 'success');
    closeLogEntryModal();
    renderLogsScreen();
  } catch(e) {
    showToast('Delete failed: ' + (e.message || 'unknown'), 'error');
  }
}
entries.deleteLogEntry = deleteLogEntry;

// ── render ─────────────────────────────────────────────
// The two big logs-screen render functions (dashboard + entries list). Moved
// verbatim from index.html (wave-3 slice 2, v124.46). The date-nav helpers,
// switchLogType, backToLogsDashboard and the shared state (logsView,
// logsDashboardSummaries, logEntries, currentLogType…) stay declared inline in
// index.html — they're read/written across bundles, so moving a declaration
// would fork the binding. These two only READ that state, so they move cleanly.
export const render = {};

// v136.8 (accessibility audit, 16 Oct 2026): the loading and failed states of the two log screens. A placeholder in
// the shape of the cards (shift-ui.css .ui-skel) instead of "Loading..."; a plain sentence and a Retry button instead
// of "Failed to load: <the server's raw error>". retryCall is a fixed call written in this file, never user text.
function logsSkeleton(label) {
  return '<div aria-busy="true" aria-label="' + label + '" style="padding:4px 0 12px">' +
    '<span class="ui-skel" style="height:56px;margin:0 0 10px"></span>' +
    '<span class="ui-skel" style="height:56px;margin:0 0 10px"></span>' +
    '<span class="ui-skel" style="height:56px;margin:0"></span></div>';
}
function logsLoadFailed(retryCall) {
  return '<div role="status" style="text-align:center;padding:24px 16px;color:var(--text)">' +
    '<div style="font-size:14px;font-weight:700;margin-bottom:4px">Couldn\'t load the logs</div>' +
    '<div style="font-size:13px;color:var(--sub);margin-bottom:12px">Check the connection, then try again.</div>' +
    '<button type="button" onclick="' + retryCall + '" style="min-height:44px;padding:0 18px;border-radius:10px;border:none;background:var(--green);color:var(--on-brand);font:inherit;font-weight:700;cursor:pointer">Retry</button></div>';
}

async function renderLogsDashboard() {
  logsView = 'dashboard';
  var dash = document.getElementById('logsDashboard');
  var body = document.getElementById('logsBody');
  var addBar = document.getElementById('logsAddBar');
  var backBar = document.getElementById('logsBackBar');
  var dateNav = document.getElementById('logsDateNav');

  if (dash) dash.style.display = '';
  if (body) body.style.display = 'none';
  if (addBar) addBar.style.display = 'none';
  if (backBar) backBar.style.display = 'none';
  if (dateNav) dateNav.style.display = 'flex';

  var dateStr = currentLogsDate();
  var viewingToday = isViewingLogsToday();
  var viewDate = new Date(dateStr + 'T12:00:00');

  document.getElementById('logsHeaderTitle').textContent = (currentStore || '').replace('ELC - ','').split(' - ').pop() + ' — Logs';
  document.getElementById('logsHeaderDate').textContent = viewDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'}) + (viewingToday ? '' : ' (past)');

  var active = getActiveLogTemplates();
  // v129.96 (Matt's 9 Aug ruling, supersedes v125.31): weekly log types are
  // visible EVERY day — they can be done any day, are due Monday, and need
  // doing once per week. Their state is judged against the whole Mon-Sun
  // week below; daily templates are untouched.
  if (!dash) return;

  if (!active.length) {
    dash.innerHTML = '<div style="text-align:center;color:var(--sub);padding:40px 20px;font-size:13px">No log types have been set up yet. Ask an admin to add some in the admin panel.</div>';
    return;
  }

  dash.innerHTML = logsSkeleton('Loading logs');   // v136.8: the shared placeholder, not "Loading..."

  // Fetch all entries for the viewed date in one shot, group by type
  try {
    // v129.96: fetch the whole Mon-Sun week of the viewed date in the SAME
    // single query (a week of one store's logs is tiny) — the day view reads
    // entry_date === dateStr as before, and weekly types read the week count.
    var wk = logWeekRange(dateStr);
    var weekRows = await DB.logs.list({
      store: currentStore,
      dateFrom: wk.from,
      dateTo: wk.to,
    });
    var byType = {};
    var weekCountByType = {};
    (Array.isArray(weekRows) ? weekRows : []).forEach(function(r){
      weekCountByType[r.log_type] = (weekCountByType[r.log_type] || 0) + 1;
      if (String(r.entry_date) !== dateStr) return;   // day cards stay day-scoped
      if (!byType[r.log_type]) byType[r.log_type] = [];
      byType[r.log_type].push(r);
    });
    logsDashboardSummaries = byType;
    var logsWeekCounts = weekCountByType;
  } catch(e) {
    console.warn('[DB] Logs dashboard load failed:', e.message);
    // v136.8 (accessibility audit): a plain sentence and a way to try again, not the raw error text
    dash.innerHTML = logsLoadFailed('Logs.render.renderLogsDashboard()');
    return;
  }

  dash.innerHTML = '';

  // Summary strip — total entries across all types for this day
  var totalEntries = Object.values(logsDashboardSummaries).reduce(function(a,arr){ return a + arr.length; }, 0);
  var strip = document.createElement('div');
  strip.style.cssText = 'background:var(--card);border-radius:14px;padding:12px 16px;margin-bottom:12px;box-shadow:0 1px 3px rgba(0,0,0,.06);display:flex;justify-content:space-between;align-items:center';
  strip.innerHTML =
    '<div style="font-size:13px;font-weight:700;color:var(--text)">' + totalEntries + ' entr' + (totalEntries === 1 ? 'y' : 'ies') + ' ' + (viewingToday ? 'today' : 'on this day') + '</div>' +
    '<div style="font-size:11px;color:var(--sub)">across ' + active.length + ' log type' + (active.length === 1 ? '' : 's') + '</div>';
  dash.appendChild(strip);

  // One card per log type
  active.forEach(function(t){
    var entries = logsDashboardSummaries[t.type] || [];
    var card = document.createElement('div');
    card.style.cssText = 'background:var(--card);border-radius:14px;padding:14px 16px;margin-bottom:10px;box-shadow:0 1px 3px rgba(0,0,0,.06);cursor:pointer;transition:transform .1s';
    card.addEventListener('mousedown', function(){ card.style.transform = 'scale(.99)'; });
    card.addEventListener('mouseup',    function(){ card.style.transform = ''; });
    card.addEventListener('mouseleave', function(){ card.style.transform = ''; });
    card.addEventListener('click', (function(type){ return function(){ switchLogType(type); }; })(t.type));

    var statusLine;
    if (isWeeklyLog(t) && !entries.length) {
      // v129.96: a weekly type with no entry on the VIEWED day speaks in
      // week terms — done this week / due today (Monday) / overdue.
      var ws = weeklyLogStatus(dateStr, logsWeekCounts[t.type] || 0);
      var wsColor = ws.state === 'done' ? 'var(--ok-deep)' : (ws.state === 'due' ? 'var(--ui-warn)' : 'var(--danger-deep)');
      statusLine =
        '<div style="font-size:12px;color:' + wsColor + ';font-weight:700">' + ws.label + '</div>' +
        '<div style="font-size:11px;color:var(--sub);margin-top:2px">Weekly — once per week, due Mondays</div>';
    } else if (entries.length) {
      // Find the most recent entry
      var sorted = entries.slice().sort(function(a,b){
        return (b.entry_time || '') > (a.entry_time || '') ? 1 : -1;
      });
      var latest = sorted[0];
      var t1 = latest.entry_time ? String(latest.entry_time).slice(0,5) : '';
      var by = latest.staff_name || latest.created_by || '—';
      statusLine =
        '<div style="font-size:12px;color:var(--ok-deep);font-weight:700">🟢 ' + entries.length + ' entr' + (entries.length === 1 ? 'y' : 'ies') + '</div>' +
        '<div style="font-size:11px;color:var(--sub);margin-top:2px">Last at ' + t1 + ' by ' + escapeHtml(by) + '</div>';
    } else {
      statusLine = '<div style="font-size:12px;color:var(--sub)">⚪ No entries ' + (viewingToday ? 'yet' : 'this day') + '</div>';
    }

    card.innerHTML =
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<div style="font-size:22px;flex-shrink:0">' + (t.icon || '📝') + '</div>' +
        '<div style="flex:1;min-width:0">' +
          '<div style="font-size:14px;font-weight:700;color:var(--text);margin-bottom:3px">' + escapeHtml(t.name || t.type) + '</div>' +
          statusLine +
        '</div>' +
        '<div style="font-size:18px;color:var(--sub);flex-shrink:0">›</div>' +
      '</div>';
    dash.appendChild(card);
  });
}
render.renderLogsDashboard = renderLogsDashboard;

async function renderLogsScreen() {
  var body = document.getElementById('logsBody');
  var addBar = document.getElementById('logsAddBar');

  if (!currentLogType) {
    body.innerHTML = '<div style="text-align:center;color:var(--sub);padding:40px 20px;font-size:13px">No log type selected. Go back to the dashboard.</div>';
    if (addBar) addBar.style.display = 'none';
    return;
  }

  var tmpl = getLogTemplate(currentLogType);
  document.getElementById('logsHeaderTitle').textContent = currentStore.replace('ELC - ','').split(' - ').pop() + ' — ' + (tmpl ? tmpl.name : currentLogType);
  // v136.5: inside a log the list is the last 30 days (today first) and every new entry is stamped with today's
  // trading day (saveLogEntry), so "+ Add Entry" always shows here. It used to follow the dashboard's date box,
  // which the drill-down hides: a stale day in it hid the button with no way back (Marina Mall, 16 Oct).
  // The subtitle says today too, so it cannot read "(past)" over today's list.
  if (addBar) addBar.style.display = 'block';
  var _hd = document.getElementById('logsHeaderDate');
  var _today = (typeof logsToday === 'function') ? logsToday() : logTradingDate();
  if (_hd) _hd.textContent = new Date(_today + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  body.innerHTML = logsSkeleton('Loading entries');   // v136.8: the shared placeholder, not "Loading..."

  // Fetch last 30 days of entries for this store + log type
  var today = new Date();
  var from = new Date(today); from.setDate(from.getDate() - 30);
  try {
    var rows = await DB.logs.list({
      store: currentStore,
      logType: currentLogType,
      dateFrom: localDateStr(from),
      dateTo: localDateStr(today),
    });
    logEntries = Array.isArray(rows) ? rows : [];
  } catch(e) {
    console.warn('[DB] Logs load failed:', e.message);
    // v136.8: plain words and Retry (this also stops a raw server message going into the page unescaped)
    body.innerHTML = logsLoadFailed('Logs.render.renderLogsScreen()');
    return;
  }

  if (!logEntries.length) {
    var msg = 'No entries yet for this log. Tap "+ Add Entry" to record the first one.';   // v136.5: the button always shows here
    body.innerHTML = '<div style="text-align:center;color:var(--sub);padding:40px 20px;font-size:13px">' + msg + '</div>';
    return;
  }

  // Group by date
  var byDate = {};
  logEntries.forEach(function(e){
    var d = e.entry_date;
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(e);
  });

  var html = '';
  Object.keys(byDate).sort().reverse().forEach(function(dStr){
    var d = new Date(dStr + 'T12:00:00');
    var dayLabel = d.toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'});
    var isToday = dStr === logTradingDate();   // v129.4 — agrees with the trading-day view
    html += '<div style="font-size:11px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.4px;margin:14px 0 6px">' + dayLabel + (isToday ? ' (today)' : '') + '</div>';
    byDate[dStr].forEach(function(entry){
      html += renderLogEntryCard(entry, tmpl);
    });
  });
  body.innerHTML = html;
}
render.renderLogsScreen = renderLogsScreen;
